import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUpRight, Phone } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SERVICES } from "@/lib/data";
import { CONTACT } from "@/lib/assets";

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          if (window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
          else el.scrollIntoView({ behavior: "smooth" });
        }, 300);
      }
    }
  }, [hash]);

  return (
    <>
      <PageHero
        eyebrow="Our Services"
        titleLines={["Six ways we", "move you forward"]}
        subtitle="The same expertise that keeps businesses across Aruba compliant, financed and growing — explained in full."
        testId="services-hero"
      />

      <div className="bg-white">
        {SERVICES.map((s, i) => {
          const reversed = i % 2 === 1;
          return (
            <section
              key={s.slug}
              id={s.slug}
              data-testid={`service-detail-${s.slug}`}
              className={`scroll-mt-24 py-20 sm:py-28 ${i % 2 === 1 ? "bg-offwhite" : "bg-white"}`}
            >
              <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                <div className={`lg:col-span-5 ${reversed ? "lg:order-2" : ""}`}>
                  <Reveal y={50}>
                    <div className="relative flex justify-center">
                      <div className="absolute inset-0 -z-0 flex items-center justify-center">
                        <div className="h-56 w-56 rounded-full bg-emerald-brand/8 blur-2xl" />
                      </div>
                      <img
                        src={s.icon}
                        alt={s.title}
                        className="relative w-56 sm:w-64 lg:w-72 object-contain drop-shadow-[0_25px_45px_rgba(9,30,54,0.25)]"
                      />
                    </div>
                  </Reveal>
                </div>

                <div className={`lg:col-span-7 ${reversed ? "lg:order-1" : ""}`}>
                  <Reveal>
                    <span className="font-heading font-extrabold text-6xl sm:text-7xl text-slate-100">
                      0{i + 1}
                    </span>
                  </Reveal>
                  <Reveal delay={0.1}>
                    <h2 className="-mt-6 font-heading font-bold text-navy tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-5xl">
                      {s.title}
                    </h2>
                  </Reveal>
                  <Reveal delay={0.2}>
                    <p className="mt-7 text-lg leading-relaxed text-slate-600 max-w-2xl">{s.body}</p>
                  </Reveal>
                  <Reveal delay={0.3}>
                    <a
                      href={`mailto:${CONTACT.email}?subject=Enquiry%20-%20${encodeURIComponent(s.title)}`}
                      data-testid={`service-cta-${s.slug}`}
                      className="group mt-9 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-sm font-semibold text-white transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_32px_-12px_rgba(9,30,54,0.5)]"
                    >
                      Discuss this service
                      <ArrowUpRight size={16} className="text-emerald-brand transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                  </Reveal>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <section data-testid="services-final-cta" className="bg-navy-900 grain py-24 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <h2 className="font-heading font-bold text-white text-4xl sm:text-5xl tracking-tight">
              Not sure where to start?
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <a
              href={`tel:${CONTACT.phone}`}
              data-testid="services-page-cta"
              className="group mt-9 inline-flex items-center gap-3 rounded-full bg-emerald-brand px-9 py-5 text-sm font-bold uppercase tracking-widest text-navy-900 transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-emerald-light hover:shadow-[0_18px_40px_-12px_rgba(16,185,129,0.6)]"
            >
              Get in Touch <Phone size={16} strokeWidth={2.5} />
            </a>
          </Reveal>
        </div>
      </section>
    </>
  );
}
